<template>
  <div class="plan-execution-monitoring nx-list-page">
    <section class="topbar">
      <div class="title-wrap">
        <div class="page-title">汽运入库计划执行全景监控</div>
        <div class="subtitle">数据月份：{{ query.queryMonth || '--' }}，更新时间：{{ lastRefreshTime || '--' }}</div>
      </div>
      <div class="top-actions">
        <span class="status-pill"><span class="dot"></span>只读监控</span>
        <el-button size="small" type="primary" icon="el-icon-refresh" :loading="refreshing" @click="loadAll">立即刷新</el-button>
      </div>
    </section>

    <section class="filter-panel">
      <div class="filter-left">
        <el-date-picker
          v-model="query.queryMonth"
          type="month"
          size="small"
          value-format="yyyy-MM"
          placeholder="选择月份"
          @change="handleSearch"
        />
        <el-select v-model="query.custNo" size="small" clearable filterable placeholder="全部货主" @change="handleSearch">
          <el-option v-for="item in custList" :key="item.id" :label="item.deptName" :value="item.id" />
        </el-select>
        <el-select v-model="query.planState" size="small" clearable placeholder="全部状态" @change="handleSearch">
          <el-option label="未开始" :value="0" />
          <el-option label="进行中" :value="1" />
          <el-option label="已完成" :value="2" />
        </el-select>
        <el-select v-model="query.riskLevel" size="small" clearable placeholder="全部风险" @change="handleRiskSearch">
          <el-option label="滞后" value="lag" />
          <el-option label="关注" value="focus" />
          <el-option label="正常" value="normal" />
          <el-option label="待执行" value="pending" />
        </el-select>
        <el-input
          v-model="query.searchValue"
          size="small"
          clearable
          placeholder="计划编号/名称/货主"
          prefix-icon="el-icon-search"
          @keyup.enter.native="handleSearch"
          @clear="handleSearch"
        />
      </div>
      <div class="filter-actions">
        <el-button size="small" @click="resetSearch">重置</el-button>
        <el-button size="small" type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
      </div>
    </section>

    <section class="metrics">
      <el-card v-for="item in metricCards" :key="item.label" class="metric-card" shadow="never">
        <div class="metric-head">
          <span>{{ item.label }}</span>
          <span :class="['metric-icon', item.color]">{{ item.icon }}</span>
        </div>
        <div class="metric-value">{{ item.value }}<span>{{ item.unit }}</span></div>
        <div class="metric-foot">{{ item.desc }}</div>
      </el-card>
    </section>

    <section class="main-grid">
      <el-card class="panel-card" shadow="never">
        <div slot="header" class="panel-head">
          <span class="panel-title">全周期执行概览</span>
          <el-tag size="mini" type="primary">本月日计划</el-tag>
        </div>
        <div v-loading="summaryLoading">
          <div class="progress-overview">
            <div class="ring" :style="ringStyle">
              <div class="ring-inner">
                <strong>{{ formatRate(summary.planProgress) }}%</strong>
                <span>整体计划完成率</span>
              </div>
            </div>
            <div class="bar-list">
              <div v-for="item in overviewBars" :key="item.label" class="bar-row">
                <span class="bar-label">{{ item.label }}</span>
                <div class="bar-track"><span :class="['bar-fill', item.color]" :style="{ width: item.percent + '%' }"></span></div>
                <strong>{{ item.value }}</strong>
              </div>
            </div>
          </div>
          <div class="state-grid">
            <div class="state-box">
              <el-tag size="mini" type="info">未开始</el-tag>
              <strong>{{ stateSummary.notStarted }} 单</strong>
              <span>待计划窗口开始</span>
            </div>
            <div class="state-box">
              <el-tag size="mini" type="warning">进行中</el-tag>
              <strong>{{ stateSummary.inProgress }} 单</strong>
              <span>正在执行运输</span>
            </div>
            <div class="state-box">
              <el-tag size="mini" type="success">已完成</el-tag>
              <strong>{{ stateSummary.completed }} 单</strong>
              <span>计划执行完成</span>
            </div>
          </div>
        </div>
      </el-card>

      <el-card class="panel-card" shadow="never">
        <div slot="header" class="panel-head">
          <span class="panel-title">风险关注清单</span>
          <el-button size="mini" @click="handleRiskSearch">刷新风险</el-button>
        </div>
        <div v-loading="riskLoading" class="risk-list">
          <div v-for="item in riskFocusList" :key="item.id || item.planNo" class="risk-item">
            <div class="risk-line">
              <span class="risk-name" :title="item.planName || item.planNo">{{ item.planNo || item.planName || '-' }}</span>
              <el-tag :type="riskType(item.riskLevel)" size="mini">{{ riskText(item.riskLevel) }}</el-tag>
            </div>
            <div class="risk-owner">{{ item.custName || item.custNo || '-' }}</div>
            <div class="risk-meta">
              <span>计划完成率 {{ formatRate(item.completeRate) }}%</span>
              <span>剩余 {{ formatNumber(item.planRemainWeight) }} 吨</span>
              <span>滞后 {{ formatRate(item.lagRate) }}%</span>
            </div>
          </div>
          <el-empty v-if="!riskFocusList.length" description="暂无风险计划" :image-size="80" />
        </div>
      </el-card>
    </section>

    <el-card class="panel-card detail-panel" shadow="never">
      <div slot="header" class="panel-head">
        <span class="panel-title">计划执行明细</span>
        <span class="detail-note">风险筛选为前端展示规则，明细数据仍以计划接口为准</span>
      </div>
      <el-table v-loading="tableLoading" :data="tableRows" border>
        <el-table-column prop="custName" label="货主" min-width="150" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.custName || row.custNo || '-' }}</template>
        </el-table-column>
        <el-table-column prop="planNo" label="计划编号" min-width="170" show-overflow-tooltip />
        <el-table-column prop="cargoName" label="物资名称" min-width="120" show-overflow-tooltip />
        <el-table-column label="计划发货量(吨)" width="135" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.planTotalWeight) }}</template>
        </el-table-column>
        <el-table-column label="已发货量(吨)" width="125" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.planExecuteWeight) }}</template>
        </el-table-column>
        <el-table-column label="未发货量(吨)" width="125" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.planRemainWeight) }}</template>
        </el-table-column>
        <el-table-column label="计划完成率" min-width="190">
          <template slot-scope="{ row }">
            <div class="progress-cell">
              <el-progress :percentage="progressPercent(row.completeRate)" :stroke-width="8" :show-text="false" />
              <span>{{ formatRate(row.completeRate) }}%</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="滞后率(%)" width="110" align="right">
          <template slot-scope="{ row }">{{ formatRate(row.lagRate) }}</template>
        </el-table-column>
        <el-table-column prop="planTruckCount" label="计划车数" width="95" align="right" />
        <el-table-column prop="completeTruckCount" label="完成车数" width="95" align="right" />
        <el-table-column label="状态" width="95" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="planStateType(row.planState)" size="mini">{{ planStateText(row.planState) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="风险" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="riskType(row.riskLevel)" size="mini">{{ riskText(row.riskLevel) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="计划开始时间" width="170">
          <template slot-scope="{ row }">{{ timeText(row.intervalStartTime) }}</template>
        </el-table-column>
        <el-table-column label="计划结束时间" width="170">
          <template slot-scope="{ row }">{{ timeText(row.intervalEndTime) }}</template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="table-pagination"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page="page.currentPage"
        :page-size="page.pageSize"
        :page-sizes="[10, 20, 50]"
        :total="page.total"
        @size-change="sizeChange"
        @current-change="currentChange"
      />
    </el-card>
  </div>
</template>

<script>
import { getCustNoList, getList, planStockAmountTotal } from './api'
import { getSettingDate } from '@/util/date'
import { unwrapDictList } from '@/util/dict-list'

const RISK_FETCH_SIZE = 500

export default {
  name: 'car-storage-plan-execution-monitoring',
  data () {
    return {
      refreshing: false,
      summaryLoading: false,
      tableLoading: false,
      riskLoading: false,
      lastRefreshTime: '',
      summary: {},
      tableRows: [],
      riskRows: [],
      custList: [],
      query: {
        queryMonth: getSettingDate('now', { format: 'YYYY-MM' }),
        custNo: '',
        planState: '',
        riskLevel: '',
        searchValue: ''
      },
      page: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  computed: {
    metricCards () {
      return [
        {
          label: '区间内累计计划量',
          value: this.formatNumber(this.summary.planTotalWeight),
          unit: '吨',
          icon: '吨',
          color: 'blue',
          desc: `已审核日计划 ${this.stateTotalPlans} 单`
        },
        {
          label: '已执行量',
          value: this.formatNumber(this.summary.planExecuteWeight),
          unit: '吨',
          icon: '执',
          color: 'green',
          desc: `完成计划 ${this.stateSummary.completed} 单`
        },
        {
          label: '未执行量',
          value: this.formatNumber(this.summary.planRemainWeight),
          unit: '吨',
          icon: '余',
          color: 'orange',
          desc: `进行中计划 ${this.stateSummary.inProgress} 单`
        },
        {
          label: '计划完成率',
          value: this.formatRate(this.summary.planProgress),
          unit: '%',
          icon: '%',
          color: 'cyan',
          desc: '已执行量 / 计划量'
        },
        {
          label: '进度滞后率',
          value: this.formatRate(this.summaryLagRate),
          unit: '%',
          icon: '滞',
          color: 'red',
          desc: `${this.lagPlanCount} 单进度滞后`
        }
      ]
    },
    summaryLagRate () {
      if (this.hasValue(this.summary.lagRate)) {
        return Number(this.summary.lagRate || 0)
      }
      const planTotalWeight = Number(this.summary.planTotalWeight || 0)
      if (!planTotalWeight || !this.riskRows.length) {
        return 0
      }
      const lagWeight = this.riskRows.reduce((sum, item) => sum + Number(item.lagWeight || 0), 0)
      return this.calcPercent(lagWeight, planTotalWeight)
    },
    ringStyle () {
      const percent = this.progressPercent(this.summary.planProgress)
      return {
        background: `conic-gradient(#2563eb 0 ${percent}%, #e8edf5 ${percent}% 100%)`
      }
    },
    overviewBars () {
      const totalWeight = Number(this.summary.planTotalWeight || 0)
      const executeWeight = Number(this.summary.planExecuteWeight || 0)
      const remainWeight = Number(this.summary.planRemainWeight || 0)
      const totalTruck = this.riskRows.reduce((sum, item) => sum + Number(item.planTruckCount || 0), 0)
      const completeTruck = this.riskRows.reduce((sum, item) => sum + Number(item.completeTruckCount || 0), 0)
      const executingTruck = this.riskRows.reduce((sum, item) => sum + Number(item.executingTruckCount || 0), 0)
      return [
        { label: '已执行量', value: `${this.formatNumber(executeWeight)} 吨`, percent: this.progressPercent(this.calcPercent(executeWeight, totalWeight)), color: 'is-blue' },
        { label: '未执行量', value: `${this.formatNumber(remainWeight)} 吨`, percent: this.progressPercent(this.calcPercent(remainWeight, totalWeight)), color: 'is-orange' },
        { label: '完成车数', value: `${this.formatNumber(completeTruck)} 辆`, percent: this.progressPercent(this.calcPercent(completeTruck, totalTruck)), color: 'is-green' },
        { label: '执行中车数', value: `${this.formatNumber(executingTruck)} 辆`, percent: this.progressPercent(this.calcPercent(executingTruck, totalTruck)), color: 'is-cyan' }
      ]
    },
    stateSummary () {
      return this.calcStateSummaryFromRows(this.riskRows)
    },
    stateTotalPlans () {
      const stateSummary = this.stateSummary
      return stateSummary.notStarted + stateSummary.inProgress + stateSummary.completed
    },
    riskFocusList () {
      return this.riskRows
        .filter(item => item.riskLevel === 'lag' || item.riskLevel === 'focus')
        .sort((a, b) => this.riskScore(b) - this.riskScore(a))
        .slice(0, 4)
    },
    lagPlanCount () {
      return this.riskRows.filter(item => item.riskLevel === 'lag').length
    }
  },
  created () {
    this.loadDictionaries()
    this.loadAll()
  },
  methods: {
    loadDictionaries () {
      getCustNoList().then(res => {
        this.custList = unwrapDictList(res)
      }).catch(() => {
        this.custList = []
      })
    },
    async loadAll () {
      this.refreshing = true
      await Promise.all([
        this.loadSummary(),
        this.loadPlans(),
        this.loadRiskRows()
      ]).finally(() => {
        this.refreshing = false
        this.lastRefreshTime = getSettingDate('now')
      })
    },
    loadSummary () {
      this.summaryLoading = true
      return planStockAmountTotal(this.baseParams()).then(res => {
        const data = this.payload(res) || {}
        const planTotalWeight = Number(data.planTotalWeight || 0)
        const planExecuteWeight = Number(data.planExecuteWeight || 0)
        const planRemainWeight = data.planRemainWeight === null || data.planRemainWeight === undefined
          ? Math.max(planTotalWeight - planExecuteWeight, 0)
          : Math.max(Number(data.planRemainWeight || 0), 0)
        const completeRate = this.hasValue(data.completeRate)
          ? Number(data.completeRate || 0)
          : this.calcPercent(planExecuteWeight, planTotalWeight)
        const timeProgress = this.hasValue(data.timeProgress)
          ? Number(data.timeProgress || 0)
          : 0
        const lagRate = this.hasValue(data.lagRate)
          ? Number(data.lagRate || 0)
          : Math.max(timeProgress - completeRate, 0)
        const lagWeight = this.hasValue(data.lagWeight)
          ? Number(data.lagWeight || 0)
          : Number(((planTotalWeight * lagRate) / 100).toFixed(2))
        this.summary = {
          ...data,
          planTotalWeight,
          planExecuteWeight,
          planRemainWeight,
          planProgress: Number(data.planProgress || completeRate || 0),
          completeRate,
          timeProgress,
          lagRate,
          lagWeight
        }
      }).catch(() => {
        this.summary = {}
        this.$message.error('计划汇总加载失败')
      }).finally(() => {
        this.summaryLoading = false
      })
    },
    loadPlans () {
      this.tableLoading = true
      const hasRiskFilter = !!this.query.riskLevel
      const requestPage = hasRiskFilter ? 1 : this.page.currentPage
      const requestSize = hasRiskFilter ? RISK_FETCH_SIZE : this.page.pageSize
      return getList({
        ...this.baseParams(),
        page: requestPage,
        size: requestSize
      }).then(res => {
        const data = this.payload(res) || {}
        const records = (data.records || []).map(this.buildPlanRow)
        if (hasRiskFilter) {
          const filtered = records.filter(item => item.riskLevel === this.query.riskLevel)
          const start = (this.page.currentPage - 1) * this.page.pageSize
          this.tableRows = filtered.slice(start, start + this.page.pageSize)
          this.page.total = filtered.length
        } else {
          this.tableRows = records
          this.page.total = data.total || 0
        }
      }).catch(() => {
        this.tableRows = []
        this.page.total = 0
        this.$message.error('计划执行明细加载失败')
      }).finally(() => {
        this.tableLoading = false
      })
    },
    loadRiskRows () {
      this.riskLoading = true
      return getList({
        ...this.baseParams(),
        page: 1,
        size: RISK_FETCH_SIZE
      }).then(res => {
        const data = this.payload(res) || {}
        this.riskRows = (data.records || []).map(this.buildPlanRow)
      }).catch(() => {
        this.riskRows = []
      }).finally(() => {
        this.riskLoading = false
      })
    },
    baseParams () {
      const searchValue = this.query.searchValue ? String(this.query.searchValue).trim() : ''
      const params = {
        planType: 2,
        queryMonth: this.query.queryMonth,
        searchValue
      }
      if (this.query.custNo) {
        params.custNo = this.query.custNo
      }
      if (this.query.planState !== '' && this.query.planState !== null && this.query.planState !== undefined) {
        params.planState = this.query.planState
      }
      return params
    },
    handleSearch () {
      this.page.currentPage = 1
      this.loadAll()
    },
    handleRiskSearch () {
      this.page.currentPage = 1
      Promise.all([this.loadPlans(), this.loadRiskRows()])
    },
    resetSearch () {
      this.query = {
        queryMonth: getSettingDate('now', { format: 'YYYY-MM' }),
        custNo: '',
        planState: '',
        riskLevel: '',
        searchValue: ''
      }
      this.page.currentPage = 1
      this.loadAll()
    },
    currentChange (currentPage) {
      this.page.currentPage = currentPage
      this.loadPlans()
    },
    sizeChange (pageSize) {
      this.page.pageSize = pageSize
      this.page.currentPage = 1
      this.loadPlans()
    },
    buildPlanRow (row) {
      const planTotalWeight = Number(row.planTotalWeight || 0)
      const planExecuteWeight = Number(row.planExecuteWeight || 0)
      const planRemainWeight = row.planRemainWeight === null || row.planRemainWeight === undefined
        ? Math.max(planTotalWeight - planExecuteWeight, 0)
        : Math.max(Number(row.planRemainWeight || 0), 0)
      const completeRate = this.hasValue(row.completeRate)
        ? Number(row.completeRate || 0)
        : this.calcPercent(planExecuteWeight, planTotalWeight)
      const timeProgress = this.hasValue(row.timeProgress)
        ? Number(row.timeProgress || 0)
        : this.calcTimeProgress(row.intervalStartTime, row.intervalEndTime)
      const lagRate = this.hasValue(row.lagRate)
        ? Number(row.lagRate || 0)
        : Math.max(timeProgress - completeRate, 0)
      const lagWeight = this.hasValue(row.lagWeight)
        ? Number(row.lagWeight || 0)
        : Number(((planTotalWeight * lagRate) / 100).toFixed(2))
      const next = {
        ...row,
        planTotalWeight,
        planExecuteWeight,
        planRemainWeight,
        planTruckCount: Number(row.planTruckCount || 0),
        completeTruckCount: Number(row.completeTruckCount || 0),
        executingTruckCount: Number(row.executingTruckCount || 0),
        completeRate,
        timeProgress,
        lagRate,
        lagWeight
      }
      next.riskLevel = this.calcRiskLevel(next)
      return next
    },
    calcStateSummaryFromRows (rows = []) {
      return rows.reduce((result, row) => {
        const state = Number(row.planState)
        if (state === 1) {
          result.inProgress += 1
        } else if (state === 2) {
          result.completed += 1
        } else {
          result.notStarted += 1
        }
        return result
      }, {
        notStarted: 0,
        inProgress: 0,
        completed: 0
      })
    },
    calcRiskLevel (row) {
      const completeRate = Number(row.completeRate || 0)
      const lagRate = Number(row.lagRate || 0)
      const start = this.parseTime(row.intervalStartTime)
      const end = this.parseTime(row.intervalEndTime)
      const now = Date.now()
      if (Number(row.planState) === 2 || completeRate >= 98) return 'normal'
      if (start && now < start) return 'pending'
      if (end && now > end) return 'lag'
      if (lagRate >= 20) return 'lag'
      if (lagRate >= 10) return 'focus'
      return 'normal'
    },
    calcTimeProgress (startTime, endTime) {
      const start = this.parseTime(startTime)
      const end = this.parseTime(endTime)
      const now = Date.now()
      if (!start || !end || end <= start) return 0
      if (now <= start) return 0
      if (now >= end) return 100
      return Number((((now - start) / (end - start)) * 100).toFixed(2))
    },
    calcPercent (value, total) {
      const n = Number(value || 0)
      const d = Number(total || 0)
      if (!d) return 0
      return Number(((n / d) * 100).toFixed(2))
    },
    progressPercent (value) {
      const num = Number(value || 0)
      return Math.max(0, Math.min(100, Number(num.toFixed(2))))
    },
    parseTime (value) {
      if (!value) return 0
      if (typeof value === 'number') return value
      const time = Date.parse(String(value).replace(/-/g, '/'))
      return Number.isNaN(time) ? 0 : time
    },
    riskScore (row) {
      const levelScore = row.riskLevel === 'lag' ? 100 : row.riskLevel === 'focus' ? 50 : 0
      return levelScore + Number(row.lagRate || 0)
    },
    hasValue (value) {
      return value !== null && value !== undefined && value !== ''
    },
    payload (res) {
      const root = res && res.data ? res.data : {}
      if (root && Object.prototype.hasOwnProperty.call(root, 'data')) {
        return root.data
      }
      return root
    },
    formatNumber (value) {
      const num = Number(value || 0)
      return num.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    formatRate (value) {
      const num = Number(value || 0)
      return num.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    timeText (value) {
      if (!value) return '-'
      return String(value).slice(0, 19)
    },
    planStateText (value) {
      const map = {
        0: '未开始',
        1: '进行中',
        2: '已完成'
      }
      return map[value] || '未开始'
    },
    planStateType (value) {
      const map = {
        0: 'info',
        1: 'warning',
        2: 'success'
      }
      return map[value] || 'info'
    },
    riskText (value) {
      const map = {
        lag: '滞后',
        focus: '关注',
        normal: '正常',
        pending: '待执行'
      }
      return map[value] || '正常'
    },
    riskType (value) {
      const map = {
        lag: 'danger',
        focus: 'warning',
        normal: 'success',
        pending: 'info'
      }
      return map[value] || 'success'
    }
  }
}
</script>

<style scoped lang="scss">
.plan-execution-monitoring {
  min-height: 100%;
  padding: 18px 20px 24px;
  background: #f5f7fb;
  color: #1f2937;
}

.topbar,
.filter-panel,
.panel-card,
.metric-card {
  border: 1px solid #e5e8ef;
  border-radius: 8px;
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 58px;
  padding: 0 18px;
  background: #fff;
}

.page-title {
  font-size: 18px;
  font-weight: 700;
}

.subtitle {
  margin-top: 4px;
  color: #6b7280;
  font-size: 12px;
}

.top-actions,
.filter-left,
.filter-actions,
.panel-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  color: #0f766e;
  background: #ccfbf1;
  border-radius: 999px;
  font-size: 12px;
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #14b8a6;
}

.filter-panel {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding: 14px 16px;
  background: #fff;
}

.filter-left {
  flex-wrap: wrap;
  flex: 1;

  ::v-deep .el-input,
  ::v-deep .el-select {
    width: 170px;
  }
}

.metrics {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
  margin-top: 14px;
}

.metric-card {
  min-height: 112px;
}

.metric-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #6b7280;
  font-size: 13px;
}

.metric-icon {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  color: #fff;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
}

.metric-icon.blue { background: #2563eb; }
.metric-icon.green { background: #16a34a; }
.metric-icon.orange { background: #d97706; }
.metric-icon.red { background: #dc2626; }
.metric-icon.cyan { background: #0891b2; }

.metric-value {
  margin-top: 12px;
  font-size: 28px;
  line-height: 1.1;
  font-weight: 800;

  span {
    margin-left: 4px;
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
  }
}

.metric-foot {
  margin-top: 9px;
  color: #6b7280;
  font-size: 12px;
}

.main-grid {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 14px;
  margin-top: 14px;
}

.panel-card {
  ::v-deep .el-card__header {
    padding: 0;
  }
}

.panel-head {
  justify-content: space-between;
  min-height: 52px;
  padding: 12px 16px;
}

.panel-title {
  font-size: 16px;
  font-weight: 700;
}

.progress-overview {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 18px;
  align-items: center;
}

.ring {
  display: grid;
  place-items: center;
  width: 190px;
  height: 190px;
  margin: 0 auto;
  border-radius: 50%;
}

.ring-inner {
  display: grid;
  place-items: center;
  width: 134px;
  height: 134px;
  text-align: center;
  background: #fff;
  border: 1px solid #e5e8ef;
  border-radius: 50%;

  strong {
    color: #2563eb;
    font-size: 30px;
  }

  span {
    color: #6b7280;
    font-size: 12px;
  }
}

.bar-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.bar-row {
  display: grid;
  grid-template-columns: 92px 1fr 105px;
  gap: 12px;
  align-items: center;
}

.bar-label {
  color: #6b7280;
}

.bar-track {
  height: 12px;
  overflow: hidden;
  background: #eef2f7;
  border-radius: 999px;
}

.bar-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
}

.bar-fill.is-blue { background: #2563eb; }
.bar-fill.is-orange { background: #d97706; }
.bar-fill.is-green { background: #16a34a; }
.bar-fill.is-cyan { background: #0891b2; }

.state-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 18px;
}

.state-box {
  padding: 12px;
  background: #fbfcfe;
  border: 1px solid #e5e8ef;
  border-radius: 8px;

  strong {
    display: block;
    margin-top: 8px;
    font-size: 22px;
  }

  span:last-child {
    color: #6b7280;
    font-size: 12px;
  }
}

.risk-list {
  min-height: 270px;
}

.risk-item {
  padding: 12px;
  background: #fbfcfe;
  border: 1px solid #e5e8ef;
  border-radius: 8px;

  & + .risk-item {
    margin-top: 10px;
  }
}

.risk-line {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.risk-name {
  overflow: hidden;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.risk-owner {
  margin-top: 5px;
  color: #6b7280;
  font-size: 12px;
}

.risk-meta {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 8px;
  color: #6b7280;
  font-size: 12px;
}

.detail-panel {
  margin-top: 14px;
}

.detail-note {
  color: #6b7280;
  font-size: 12px;
}

.progress-cell {
  display: grid;
  grid-template-columns: 1fr 54px;
  gap: 8px;
  align-items: center;
}

.table-pagination {
  margin-top: 14px;
  text-align: right;
}

@media (max-width: 1360px) {
  .metrics {
    grid-template-columns: repeat(3, 1fr);
  }

  .main-grid {
    grid-template-columns: 1fr;
  }
}
</style>
